import { faFaucet } from '@fortawesome/free-solid-svg-icons/faFaucet';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons/faWarehouse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HandymanIcon from '@mui/icons-material/Handyman';
import HomeIcon from '@mui/icons-material/Home';
import PoolIcon from '@mui/icons-material/Pool';

import { useTheme } from '@mui/material';
import React from 'react';

import { TradeEnum } from '_lib/api/apiTypes.shim';
import { Stack } from '_lib/components/Stack/Stack';
import { SvgIconProps } from '_lib/components/SvgIcon/SvgIcon';
import { Typography } from '_lib/components/Typography/Typography';

export const TradeIcon = (props: { trade: TradeEnum; color?: SvgIconProps['color'] }) => {
  const { trade, color } = props;

  const theme = useTheme();

  const toFaColor = (color?: SvgIconProps['color']) => {
    if (!color) return color;

    if (color in theme.palette) {
      const paletteColor = theme.palette[color as keyof typeof theme.palette];
      if (typeof paletteColor === 'object' && 'main' in paletteColor) return paletteColor.main;
      if (typeof paletteColor === 'object' && 'active' in paletteColor) return paletteColor.active;
    }

    return theme.palette.primary.main;
  };

  if (trade === TradeEnum.Electric) {
    return <FlashOnIcon fontSize='small' color={color} />;
  } else if (trade === TradeEnum.HVAC) {
    return <AcUnitIcon fontSize='small' color={color} />;
  } else if (trade === TradeEnum.Plumbing) {
    return (
      <FontAwesomeIcon
        icon={faFaucet}
        color={toFaColor(color)}
        style={{ width: '20px', height: '20px', padding: 0.75 }}
      />
    );
  } else if (trade === TradeEnum.Roofing) {
    return <HomeIcon fontSize='small' color={color || 'action'} />;
  } else if (trade === TradeEnum.Restoration) {
    return <HandymanIcon fontSize='small' color={color} />;
  } else if (trade === TradeEnum.Garage) {
    return (
      <FontAwesomeIcon
        icon={faWarehouse}
        color={toFaColor(color)}
        style={{ width: '20px', height: '20px', padding: 0.75 }}
      />
    );
  } else if (trade === TradeEnum.Pools) {
    return <PoolIcon fontSize='small' color={color} />;
  }
  return <></>;
};

type TradesDisplayProps = {
  trades: [TradeEnum, ...TradeEnum[]];
  color?: SvgIconProps['color'];
};

export const TradesDisplay = (props: TradesDisplayProps) => {
  const { trades, color } = props;

  const tradesToRender = [];

  if (trades.includes(TradeEnum.Electric)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Electric} color={color} />
        <Typography text='Electric' />
      </>
    );
  }

  if (trades.includes(TradeEnum.HVAC)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.HVAC} color={color} />
        <Typography text='HVAC' />
      </>
    );
  }

  if (trades.includes(TradeEnum.Plumbing)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Plumbing} color={color} />
        <Typography text='Plumbing' />
      </>
    );
  }

  if (trades.includes(TradeEnum.Roofing)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Roofing} color={color || 'action'} />
        <Typography text='Roofing' />
      </>
    );
  }

  if (trades.includes(TradeEnum.Restoration)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Restoration} color={color} />
        <Typography text='Restoration' />
      </>
    );
  }

  if (trades.includes(TradeEnum.Garage)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Garage} color={color} />
        <Typography text='Garage' />
      </>
    );
  }

  if (trades.includes(TradeEnum.Pools)) {
    tradesToRender.push(
      <>
        <TradeIcon trade={TradeEnum.Pools} color={color} />
        <Typography text='Pools' />
      </>
    );
  }

  return (
    <Stack useFlexGap spacing={1} flexWrap='wrap' direction='row'>
      {tradesToRender.map((element, index) => (
        <Stack key={index} direction='row' alignItems='center' useFlexGap spacing={0.5}>
          {element}
        </Stack>
      ))}
    </Stack>
  );
};
